import { CheckCircle, Clear, Warning } from '@material-ui/icons';
import styled from 'styled-components';
import { Colors } from '../constants/colors';
import { IconButton } from './buttons/IconButton';
import { CircularProgress, createStyles, InputBase, MenuItem, Select, Theme, Tooltip, withStyles } from '@material-ui/core';
import React from 'react';
import { fileCategories } from '../constants/files';
import { UserDocument } from '../model/UserDocument';
import { Fonts } from '../constants/fonts';
import { RequestState } from '../model/enums/RequestState';
import { defaultErrorMessage } from '../constants/messages';
import { darken } from 'polished';

type FileItemProps = {
  className?: string,
  document: UserDocument,
  onDelete: () => void
  onCategoryChange: (category: string) => void
}

const CategorySelect = withStyles((theme: Theme) =>
  createStyles({
    root: {
      lineHeight: '50px',
      width: '40%'
    },
    input: {
      borderRadius: 0,
      backgroundColor: Colors.white,
      borderLeft: '1px solid ' + Colors.borderColor,
      fontSize: 16,
      fontFamily: Fonts.base,
      padding: '0 0 0 12px',
      paddingRight: '30px !important',
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      '&:focus': {
        borderRadius: 0,
        backgroundColor: Colors.white,
        boxShadow: 'none'
      }
    }
  })
)(InputBase);

export const FileItem = (props: FileItemProps) => (
  <Wrapper>
    <FileUploadState>
      {(() => {
        switch (props.document.uploadState) {
          case RequestState.WAITING:
            return <Tooltip title='Čeká na nahrání' arrow>
              <div className='waiting' />
            </Tooltip>;
          case RequestState.LOADING:
            return <Tooltip title='Probíhá nahrání souboru' arrow>
              <CircularProgress className='loading'
                                size={20} />
            </Tooltip>;
          case RequestState.ERROR:
            return <Tooltip title={props.document.errorMessage ?? defaultErrorMessage} arrow>
              <Warning className='warning' />
            </Tooltip>;
          default:
            return <Tooltip title='Soubor byl úspěšně nahrán' arrow>
              <CheckCircle className='success' />
            </Tooltip>;
        }
      })()}
    </FileUploadState>
    <StyledFileItem className={props.document.uploadState.valueOf()}>
      <strong>{props.document.file.name}</strong>
      {props.document.uploadState === RequestState.SUCCESS &&
      <Select value={props.document.category}
              onChange={(e) => props.onCategoryChange(e.target.value as string)}
              input={<CategorySelect />}>
        {fileCategories.map((category, key) =>
          <MenuItem key={key} value={category}>
            {category}
          </MenuItem>
        )}
      </Select>}
    </StyledFileItem>
    <IconButton onClick={props.onDelete}>
      <Tooltip title='Smazat soubor' arrow>
        <Clear />
      </Tooltip>
    </IconButton>
  </Wrapper>
);

const StyledFileItem = styled.div`
  background: ${Colors.white};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid ${Colors.borderColor};
  min-height: 50px;

  opacity: 0.5;
  filter: grayscale(1);
  pointer-events: none;

  transition: opacity 0.2s ease-in-out;

  strong {
    flex: 1;
    flex-basis: calc(60% - 25px);
    width: calc(60% - 25px);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    margin: 0 25px;
  }

  &.success {
    opacity: 1;
    filter: none;
    pointer-events: all;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  ${StyledFileItem} {
    flex: 1;
  }

  ${IconButton} {
    width: 50px;
    display: flex;
    justify-content: center;
    align-items: center;

    :hover {
      background: none !important;
      color: ${darken(0.1, Colors.danger)};
    }
  }
`;

const FileUploadState = styled.div`
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;

  .warning {
    color: ${Colors.warning}
  }

  .success {
    color: ${Colors.success}
  }

  .loading {
    color: ${Colors.grey}
  }

  .waiting {
    width: 10px;
    height: 10px;
    border-radius: 5px;
    border: 2px solid ${Colors.grey}
  }
`;

